/**
 * Generates a script template from a role description, or revises the template
 * already in the form. Backed by POST /api/script_template_generations
 * (Api::ScriptTemplateGenerationsController); the modal awaits one promise and
 * reads failures through extractErrorMessage from the `{ errors: [] }` envelope.
 */
import { newAxiosClient, type IScriptTemplate } from "@shadowcue/ui";
import { scriptTemplateToPayload } from "../form_helpers";

interface modalValues {
  title: string,
  subtitle: string,
  field: string,
  submit: string,
  dismiss: string,
  progressTitle: string
}

const initialModalValues = {
  title: "Start a new script",
  subtitle:
    "Describe the role and we'll draft the interview script, or fill the form in yourself.",
  field: "Describe the role",
  submit: "Generate script",
  dismiss: "Start from scratch",
  progressTitle: "Drafting your script…",
}

const regenerateModalValues = {
  title: "Regenerate script",
  subtitle:
    "Describe what to change and we'll build on the script you already have.",
  field: "What should change?",
  submit: "Regenerate",
  dismiss: "Cancel",
  progressTitle: "Revising your script…",
}

export const getModalValuesFromMode = (mode: generateMode): modalValues => {
  switch (mode) {
    case generateMode.INITIAL:
      return initialModalValues
    case generateMode.REGENERATE:
      return regenerateModalValues
  }
}

export enum generateMode {
  INITIAL = "initial",
  REGENERATE = "regenerate"
}

export interface IGenerateArgs {
  prompt: string;
  /**
   * The script as it currently stands. When present the prompt is a
   * refinement — the backend revises this whole template rather than
   * starting over, and the result replaces the form.
   */
  scriptTemplate: IScriptTemplate;
  authToken: string;
  /** Lets the caller cancel the request, e.g. when the page unmounts mid-flight. */
  signal?: AbortSignal;
}

export const generateScriptTemplate = async ({
  prompt,
  scriptTemplate,
  authToken,
  signal,
}: IGenerateArgs): Promise<IScriptTemplate> => {
  const axios = newAxiosClient();
  const { data } = await axios.post<{ scriptTemplate: IScriptTemplate }>(
    "/api/script_templates/generate",
    {
      prompt,
      ...scriptTemplateToPayload(scriptTemplate),
    },
    {
      headers: { AUTHORIZATION: `Bearer ${authToken}` },
      withCredentials: true,
      // Just above the server's own 120s bound, so a real timeout still
      // arrives with its specific message while a dead socket can't leave the
      // modal building forever for someone who never thinks to cancel.
      timeout: 130_000,
      signal,
    },
  );
  return data.scriptTemplate;
};
